"use client";

import { useEffect, useMemo, useState } from "react";
import {
  cloneKeyBindings,
  DEFAULT_KEY_BINDINGS,
  formatKeyCodes,
  KEYBOARD_ACTION_LABELS,
  KEYBOARD_ACTIONS,
  type KeyBindings,
} from "../hooks/useLegacyKeyboard";
import type { LegacyAudioSettings } from "../hooks/useLegacyAudio";
import {
  DEFAULT_TOUCH_CONTROLS_LAYOUT,
  type TouchControlsSettings,
} from "../hooks/useTouchControlsSettings";
import { normalizePlayerProfile, ROOM_PROFILE_MAX_LENGTH } from "../profile";
import { TouchLayoutEditor } from "./TouchLayoutEditor";

type KeyConfigPanelProps = {
  audioSettings: LegacyAudioSettings;
  bindings: KeyBindings;
  profileId: string;
  profileName: string;
  touchControlsSettings: TouchControlsSettings;
  onBack: () => void;
  onAudioSettingsChange: (settings: LegacyAudioSettings) => void;
  onProfileIdChange: (value: string) => void;
  onProfileNameChange: (value: string) => void;
  onTouchControlsSettingsChange: (settings: TouchControlsSettings) => void;
  onChange: (bindings: KeyBindings) => void;
};

export function KeyConfigPanel({
  audioSettings,
  bindings,
  profileId,
  profileName,
  touchControlsSettings,
  onBack,
  onAudioSettingsChange,
  onProfileIdChange,
  onProfileNameChange,
  onTouchControlsSettingsChange,
  onChange,
}: KeyConfigPanelProps) {
  const [listening, setListening] = useState<keyof KeyBindings | null>(null);
  const [message, setMessage] = useState("ボタンを押してから、割り当てたいキーを入力してください");

  useEffect(() => {
    if (!listening) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.code === "Escape") {
        setListening(null);
        setMessage("キー割り当てをキャンセルしました");
        return;
      }
      if (!event.code) return;

      const conflict = KEYBOARD_ACTIONS.find(
        (action) => action !== listening && bindings[action].includes(event.code),
      );
      if (conflict) {
        setMessage(`${formatKeyCodes([event.code])} は「${KEYBOARD_ACTION_LABELS[conflict]}」に設定済みです`);
        return;
      }

      const next = cloneKeyBindings(bindings);
      next[listening] = [event.code];
      onChange(next);
      setListening(null);
      setMessage(`${KEYBOARD_ACTION_LABELS[listening]}を${formatKeyCodes([event.code])}に設定しました`);
    };

    window.addEventListener("keydown", handleKeyDown, { capture: true, passive: false });
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [bindings, listening, onChange]);

  const currentActionLabel = useMemo(
    () => (listening ? KEYBOARD_ACTION_LABELS[listening] : ""),
    [listening],
  );

  const reset = () => {
    onChange(cloneKeyBindings(DEFAULT_KEY_BINDINGS));
    setListening(null);
    setMessage("初期設定に戻しました");
  };

  return (
    <main className="settings-shell">
      <header className="mode-header settings-header">
        <button className="back-button" onClick={onBack} type="button">← メニュー</button>
        <span className="mode-header-label">KEY CONFIG</span>
      </header>

      <section aria-labelledby="key-config-title" className="settings-panel">
        <p className="brand-kicker">PC / EXTERNAL KEYPAD</p>
        <h1 className="brand-title" id="key-config-title">キーコンフィグ</h1>
        <p className="settings-lead">各ボタンを押してから、割り当てたいキーを入力します。設定はこの端末に保存されます。</p>

        <section aria-labelledby="profile-settings-title" className="profile-settings">
          <div className="key-config-action">
            <strong id="profile-settings-title">プロフィール入力</strong>
            <small>ルーム入室時に自動入力</small>
          </div>
          <label className="profile-settings-field">
            <span>NAME <small>名前10文字まで #トリップキー（任意）</small></span>
            <input
              aria-label="名前とトリップ"
              maxLength={ROOM_PROFILE_MAX_LENGTH}
              onChange={(event) => onProfileNameChange(normalizePlayerProfile(event.target.value))}
              placeholder="名前#トリップキー"
              type="text"
              value={profileName}
            />
          </label>
          <label className="profile-settings-field">
            <span>ID <small>戦績保存用</small></span>
            <input
              aria-label="戦績保存用ID"
              maxLength={64}
              onChange={(event) => onProfileIdChange(event.target.value)}
              placeholder="自分で決めたID"
              type="text"
              value={profileId}
            />
          </label>
          <small className="settings-hint">入力内容はこの端末に自動保存され、次回のルーム入室時に使われます。</small>
        </section>

        <section aria-labelledby="audio-settings-title" className="audio-settings">
          <div className="audio-settings-heading">
            <div className="key-config-action">
              <strong id="audio-settings-title">効果音</strong>
              <small>対戦・練習・ルーム共通</small>
            </div>
            <button
              aria-pressed={audioSettings.muted}
              className={`audio-toggle${audioSettings.muted ? " is-muted" : ""}`}
              onClick={() => onAudioSettingsChange({ ...audioSettings, muted: !audioSettings.muted })}
              type="button"
            >
              {audioSettings.muted ? "ミュート ON" : "ミュート OFF"}
            </button>
          </div>

          <label className="volume-control">
            <span>音量</span>
            <input
              aria-label="効果音の音量"
              max="100"
              min="0"
              onChange={(event) => onAudioSettingsChange({
                ...audioSettings,
                volume: Number(event.target.value) / 100,
              })}
              type="range"
              value={Math.round(audioSettings.volume * 100)}
            />
            <output>{Math.round(audioSettings.volume * 100)}%</output>
          </label>
        </section>

        <section aria-labelledby="touch-settings-title" className="touch-settings">
          <div className="audio-settings-heading">
            <div className="key-config-action">
              <strong id="touch-settings-title">スマホ操作</strong>
              <small>盤面タッチ／ボタン／ジョイスティックを切り替え</small>
            </div>
            <button
              aria-pressed={touchControlsSettings.visible}
              className={`audio-toggle${touchControlsSettings.visible ? "" : " is-muted"}`}
              onClick={() => onTouchControlsSettingsChange({
                ...touchControlsSettings,
                visible: !touchControlsSettings.visible,
              })}
              type="button"
            >
              {touchControlsSettings.visible ? "表示 ON" : "非表示 OFF"}
            </button>
          </div>

          <fieldset aria-label="スマホ操作方式" className="touch-input-mode">
            <legend>操作方式</legend>
            <div className="touch-input-mode-options">
              <label className="touch-input-mode-option">
                <input
                  checked={touchControlsSettings.inputMode === "gesture"}
                  name="touch-input-mode"
                  onChange={() => onTouchControlsSettingsChange({
                    ...touchControlsSettings,
                    inputMode: "gesture",
                  })}
                  type="radio"
                  value="gesture"
                />
                <span>
                  <strong>盤面タッチ</strong>
                  <small>スワイプ移動・タップ回転</small>
                </span>
              </label>
              <label className="touch-input-mode-option">
                <input
                  checked={touchControlsSettings.inputMode === "buttons"}
                  name="touch-input-mode"
                  onChange={() => onTouchControlsSettingsChange({
                    ...touchControlsSettings,
                    inputMode: "buttons",
                  })}
                  type="radio"
                  value="buttons"
                />
                <span>
                  <strong>ボタン操作</strong>
                  <small>左・下・右のボタン</small>
                </span>
              </label>
              <label className="touch-input-mode-option">
                <input
                  checked={touchControlsSettings.inputMode === "joystick"}
                  name="touch-input-mode"
                  onChange={() => onTouchControlsSettingsChange({
                    ...touchControlsSettings,
                    inputMode: "joystick",
                  })}
                  type="radio"
                  value="joystick"
                />
                <span>
                  <strong>ジョイスティック</strong>
                  <small>レバーを倒して移動・落下</small>
                </span>
              </label>
            </div>
          </fieldset>

          <label className="volume-control">
            <span>透明度</span>
            <input
              aria-label="タッチ操作の透明度"
              max="100"
              min="0"
              onChange={(event) => onTouchControlsSettingsChange({
                ...touchControlsSettings,
                transparency: Number(event.target.value) / 100,
              })}
              type="range"
              value={Math.round(touchControlsSettings.transparency * 100)}
            />
            <output>{Math.round(touchControlsSettings.transparency * 100)}%</output>
          </label>
          <small className="settings-hint">操作方式は対戦画面にすぐ反映されます。非表示にすると入力も無効になります。</small>
        </section>

        <section aria-labelledby="touch-feature-settings-title" className="touch-feature-settings">
          <div className="key-config-action">
            <strong id="touch-feature-settings-title">スマホ専用機能</strong>
            <small>盤面タッチ時の補助表示・落下操作</small>
          </div>
          <div className="touch-feature-options">
            <label className="touch-feature-control">
              <input
                checked={touchControlsSettings.ghostVisible}
                onChange={(event) => onTouchControlsSettingsChange({
                  ...touchControlsSettings,
                  ghostVisible: event.target.checked,
                })}
                type="checkbox"
              />
              <span>
                <strong>ゴースト</strong>
                <small>落下予定位置を表示</small>
              </span>
            </label>
            <label className="touch-feature-control">
              <input
                checked={touchControlsSettings.hardDropEnabled}
                onChange={(event) => onTouchControlsSettingsChange({
                  ...touchControlsSettings,
                  hardDropEnabled: event.target.checked,
                })}
                type="checkbox"
              />
              <span>
                <strong>ハードドロップ</strong>
                <small>素早い下フリック</small>
              </span>
            </label>
          </div>
          <small className="settings-hint">この2項目はスマホ操作にだけ反映されます。</small>
        </section>

        <section aria-labelledby="touch-layout-title" className="touch-layout-settings">
          <div className="key-config-action">
            <strong id="touch-layout-title">ボタン配置</strong>
            <small>全画面で縦・横それぞれの位置を設定</small>
          </div>
          <TouchLayoutEditor
            onChange={onTouchControlsSettingsChange}
            settings={touchControlsSettings}
          />
          <button
            className="secondary-button touch-layout-reset"
            onClick={() => onTouchControlsSettingsChange({
              ...touchControlsSettings,
              layout: DEFAULT_TOUCH_CONTROLS_LAYOUT,
              portraitLayout: DEFAULT_TOUCH_CONTROLS_LAYOUT,
              landscapeLayout: DEFAULT_TOUCH_CONTROLS_LAYOUT,
            })}
            type="button"
          >
            ボタン配置を初期化
          </button>
          <small className="settings-hint">左右は1マス入力、下は押している間だけソフトドロップします。</small>
        </section>

        <div className="key-config-list">
          {KEYBOARD_ACTIONS.map((action) => {
            const active = listening === action;
            return (
              <div className="key-config-row" key={action}>
                <div className="key-config-action">
                  <strong>{KEYBOARD_ACTION_LABELS[action]}</strong>
                  <small>{action === "softDrop" ? "押している間だけ高速落下" : "ゲーム中の操作"}</small>
                </div>
                <button
                  aria-label={`${KEYBOARD_ACTION_LABELS[action]}のキーを変更`}
                  className={`key-capture-button${active ? " listening" : ""}`}
                  onClick={() => {
                    setListening(action);
                    setMessage(`${KEYBOARD_ACTION_LABELS[action]}に割り当てるキーを押してください（Escでキャンセル）`);
                  }}
                  type="button"
                >
                  {active ? `入力待ち: ${currentActionLabel}` : formatKeyCodes(bindings[action])}
                </button>
              </div>
            );
          })}
        </div>

        <p aria-live="polite" className="settings-message" role="status">{message}</p>
        <div className="settings-actions">
          <button className="secondary-button" onClick={reset} type="button">初期設定に戻す</button>
          <button className="chrome-button" onClick={onBack} type="button">完了</button>
        </div>
      </section>
    </main>
  );
}
